import React, { useEffect, useState } from 'react';
import cn from 'classnames';

import {
	DynamicIconWrapper,
	Icons,
	Modal,
	Select,
	Tooltip } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import ProfileTabSection from '@components/common/ProfileTabSection';
import FileTypeSelectionForm from '@components/ModalComponents/FileTypeSelectionForm';
import { THandleExport } from '@components/ModalComponents/FileTypeSelectionForm/FileTypeSelectionForm';
import AllCellsTab from '@components/routes/AdminPanel/ManageAllCells/AllCellsTab';

import { useMutateExportAllSites } from '@hooks/sites/useMutateExportAllSites';
import { useManageSitesOptions } from '@hooks/useManageSitesOptions';

import { IExportAllCellsDTO } from '@interfaces/cells/dto/cellsDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';

import {
	EAdminPanelPageTitles,
	EMenuActionsTypes,
	ETechnologiesValues,
	TTechnologiesValues,
	TVendorsValues,
} from '@enums/common';

import { exportRadioButtonsData } from '@constants/common';
import { MODAL_WIDTH_455 } from '@constants/modals';
import { technologyTabs } from '@components/routes/AdminPanel/SiteProfile/constants';

import styles from './ManageAllCells.module.scss';

const ManageAllCells = () => {
	const [currentTab, setCurrentTab] = useState<TTechnologiesValues | null>(null);
	const [selectedVendor, setSelectedVendor] = useState<TSelectOption>(null);

	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);

	const { vendorsOptions } = useManageSitesOptions();

	const onVendorChange = (option: TSelectOption) => {
		setSelectedVendor(option);

		if (!option?.value) return;

		setCurrentTab(ETechnologiesValues.T_4G_TDD);
	};

	const toggleFileTypeSelectionModal = () => setIsOpenFileTypeSelectionModal(prevState => !prevState);

	const {
		handlePreparingExportAllSites,
		isLoadingExportAllSites,
	} = useMutateExportAllSites();

	const handlePreparingExport = ({ timeZoneMinutes, fileType }: IExportAllCellsDTO) => {
		const dto = {
			timeZoneMinutes,
			fileType,
		};
		const vendor = selectedVendor?.value as string;

		if (vendor) handlePreparingExportAllSites({ vendor: vendor, dto });
	};

	const getCurrentTab = () => {
		if (!currentTab) return null;

		return ETechnologiesValues[currentTab] &&
			<AllCellsTab
				technologyValue={ETechnologiesValues[currentTab]}
				vendor={selectedVendor?.value as TVendorsValues}
			/>;
	};

	useEffect(() => {
		if (vendorsOptions.length) {
			onVendorChange(vendorsOptions[0]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className={styles.manageAllSitesContainer}>
			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={toggleFileTypeSelectionModal}
				title='File extension to export'
				width={MODAL_WIDTH_455}
			>
				<FileTypeSelectionForm
					activeGroupTableAction={EMenuActionsTypes.all}
					closeModals={toggleFileTypeSelectionModal}
					exportRadioButtonsData={exportRadioButtonsData}
					handlePreparingExport={handlePreparingExport as THandleExport}
					isLoading={isLoadingExportAllSites}
					toggleModal={toggleFileTypeSelectionModal}
				/>
			</Modal>

			<PageTitle
				icon={Icons.manageAllSiteDBInverse()}
				title={EAdminPanelPageTitles.AllSitesCells}
			/>
			<div className={cn(styles.whiteWrapper, {
				[styles.technologyWrapper]: !selectedVendor,
			})}>
				<div className={styles.title}>
					Please choose vendor:
				</div>
				<div className={styles.selectWrapper}>
					<Select
						handleChange={onVendorChange as never}
						isWithoutUseForms
						label='Vendor'
						name='vendor'
						options={vendorsOptions}
						placeholder='Select vendor'
						value={selectedVendor}
						width='240px'
					/>
					<div
						className={styles.export}
						onClick={selectedVendor ? toggleFileTypeSelectionModal : undefined}
					>
						<Tooltip content='Export all'>
							<div>
								<DynamicIconWrapper
									icon={Icons.export}
									isDisabled={!selectedVendor}
								/>
							</div>
						</Tooltip>
					</div>
				</div>
				{selectedVendor && currentTab
					&& <div className={styles.table}>
						<ProfileTabSection
							currentTab={currentTab}
							getCurrentTab={getCurrentTab}
							isFullHeight
							isWithoutShadow
							maxWidth='none'
							setCurrentTab={setCurrentTab}
							tabWidth='170px'
							tabs={technologyTabs}
						/>
					</div>
				}
			</div>
		</div>
	);
};

export default ManageAllCells;
